import { render } from '@testing-library/react';
import renderer from 'react-test-renderer';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MemoryRouter, MemoryRouterProps } from 'react-router-dom';
import { factory } from 'shared/utils/utils';
import { DialogProvider } from 'ogp/providers/dialog-provider';
import { AlertProvider } from 'shared/components/alerts';
import { AppSettingsProvider } from 'shared/contexts/AppSettingsContext';
import { ThemeProvider } from 'shared/contexts/theme-provider';
import { FeatureFlagProvider } from 'shared/contexts/feature-flag-provider/feature-flag-provider';

type ProviderWithProps<
  P extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<React.ComponentProps<P>>,
  Props = React.ComponentProps<P>,
> = {
  provider: P;
  props?: Omit<Props, 'children'>; // we're going to provide children ourselves
};

type OptionalProviders = Partial<{
  dialogProvider: ProviderWithProps<typeof DialogProvider>;
  featureFlagProvider: ProviderWithProps<typeof FeatureFlagProvider>;
}>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: -1,
      retry: false, // disable retries otherwise tests will never pass
    },
  },
});

const initialProviders: {
  themeProvider: ProviderWithProps<typeof ThemeProvider>;
  appSettingsProvider: ProviderWithProps<typeof AppSettingsProvider>;
  alertProvider: ProviderWithProps<typeof AlertProvider>;
  queryClientProvider: ProviderWithProps<typeof QueryClientProvider>;
  memoryRouterProvider: ProviderWithProps<typeof MemoryRouter, MemoryRouterProps>;
  dialogProvider: ProviderWithProps<typeof DialogProvider>;
} = {
  memoryRouterProvider: {
    provider: MemoryRouter,
    props: {
      // TODO make this dynamic so we support both OGP and HQ themes
      initialEntries: ['/ogp'],
    },
  },
  queryClientProvider: {
    provider: QueryClientProvider,
    props: {
      client: queryClient,
    },
  },
  appSettingsProvider: {
    provider: AppSettingsProvider,
  },
  themeProvider: {
    provider: ThemeProvider,
  },
  alertProvider: {
    provider: AlertProvider,
  },
  dialogProvider: {
    provider: DialogProvider,
  },
};

const factories = {
  alertProvider: factory(initialProviders.alertProvider),
  memoryRouterProvider: factory(initialProviders.memoryRouterProvider),
  queryClientProvider: factory(initialProviders.queryClientProvider),
  dialogProvider: factory({
    provider: DialogProvider,
  }),
  featureFlagProvider: factory({ provider: FeatureFlagProvider }),
};

const providersFactory = factory<typeof initialProviders & OptionalProviders>(initialProviders);

function _renderProviders<T extends typeof render>(
  renderFn: T
): (
  component: React.ReactNode,
  providers?: ReturnType<typeof providersFactory>
) => ReturnType<typeof render>;
function _renderProviders<T extends typeof renderer.create>(
  renderFn: T
): (
  component: React.ReactNode,
  providers?: ReturnType<typeof providersFactory>
) => ReturnType<typeof renderer.create>;
function _renderProviders<T extends (...args: unknown[]) => ReturnType<T>>(renderFn: T) {
  return (component: React.ReactNode, providers = providersFactory()) => {
    const values = Object.values(providers).map(
      ({ provider: Provider, props = {} }) =>
        ({ children }: React.PropsWithChildren<unknown>) => (
          // @ts-expect-error items coming from Object.values are typed as a union type and I really have no clue how to type this
          <Provider {...props}>{children}</Provider>
        )
    );

    const Providers = values.reduce(
      (GlobalProvider, NestedProvider) =>
        ({ children, ...props }: React.PropsWithChildren<unknown>) => (
          <GlobalProvider>
            <NestedProvider {...props}>{children}</NestedProvider>
          </GlobalProvider>
        )
    );

    return renderFn(<Providers>{component}</Providers>);
  };
}

const renderProvidersForSnapshots = _renderProviders(renderer.create);
const renderProviders = _renderProviders(render);

export { queryClient, renderProviders, renderProvidersForSnapshots, providersFactory, factories };
